'use client';

import { UserPlus, Play, CheckCircle2, Users, Star, BarChart3, Shield, Zap, DollarSign, TrendingUp, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

const Page = () => {
  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(168,85,247,0.05),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(236,72,153,0.05),transparent_50%)]"></div>
      
      <div className="container mx-auto px-4 relative z-10 flex items-center justify-center" style={{ minHeight: '80vh' }}>
        <div className="max-w-5xl mx-auto text-center py-12">
          {/* Main Heading */}
          <div className="mb-8 animate-fade-in">
            {/* Tagline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-gray-900">Welcome to </span>
              <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-purple-600 bg-clip-text text-transparent">Tblondi</span>
              </h1>
            
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-6 leading-relaxed">
              <span className="text-gray-700">Influencer marketplace for campaign automation and </span>
              <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-purple-600 bg-clip-text text-transparent">data-driven analytics</span>
            </h2>
            
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Modular and Data driven Creators Advertising Solution. Maximise profit margins thanks to process automatisation and unique advertising assets inventory.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <Link href="/sign-up">
              <Button 
                size="lg"
                className="relative bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium px-8 py-6 rounded-lg text-base shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Get started
                </span>
                {/* Shine effect */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
              </Button>
            </Link>
            
            <Link href="/v1/demo/creators">
                <Button 
                  size="lg"
                  variant="ghost"
                  className="relative bg-white hover:bg-purple-600 text-purple-600 hover:text-white font-medium px-8 py-6 rounded-lg text-base border-2 border-purple-600 hover:border-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <Play className="h-5 w-5" />
                    Demo
                  </span>
                  {/* Shine effect */}
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-0 group-hover:opacity-30 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                </Button>
            </Link>
          </div>

        </div>
      </div>

      {/* For Brands Section - Full Width */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(59,130,246,0.02),transparent_50%)]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="animate-slide-in-left">
                <div className="inline-block mb-6">
                  <span className="bg-pink-100 text-pink-600 text-sm font-medium px-4 py-2 rounded-full">
                    For Brands
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12">
                  Find ideal ambassadors for your brand
                </h2>
                
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Smart Creator Discovery</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                      Discover perfect — fit creators with data-backed insights.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Zap className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Campaign Control</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                      Launch, approve, and track campaigns — all in one place.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Trust & Transparency</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                      Transparent pricing, brand safety, and smart reporting.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Visual */}
              <div className="relative animate-slide-in-right">
                <div className="bg-white rounded-3xl p-8 shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-700">Campaign Reach</h3>
                    <TrendingUp className="h-5 w-5 text-green-500" />
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Instagram</span>
                        <span className="text-sm font-bold text-pink-600">2.3M</span>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500" style={{ width: '85%' }}></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">YouTube</span>
                        <span className="text-sm font-bold text-blue-600">1.8M</span>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600" style={{ width: '70%' }}></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">TikTok</span>
                        <span className="text-sm font-bold text-purple-600">1.1M</span>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500" style={{ width: '55%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <hr className="border-t border-gray-200" />
        </div>
      </div>

      {/* For Creators Section - Full Width */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(6,182,212,0.02),transparent_50%)]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Right Visual - shows first on mobile, second on desktop */}
              <div className="relative order-2 lg:order-1 animate-slide-in-left" style={{ animationDelay: '0.3s' }}>
                <div className="bg-white rounded-3xl p-8 shadow-xl">
                  <h3 className="text-lg font-semibold text-gray-700 mb-6">Active Campaigns</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-2xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-pink-600"></div>
                        <div>
                          <p className="font-semibold text-gray-900">Fashion Campaign</p>
                          <p className="text-sm text-gray-600">Active 5 days</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-pink-600">$2,450</span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600"></div>
                        <div>
                          <p className="font-semibold text-gray-900">Tech Review</p>
                          <p className="text-sm text-gray-600">Completed</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-blue-600">$1,890</span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-red-500"></div>
                        <div>
                          <p className="font-semibold text-gray-900">Food Brand</p>
                          <p className="text-sm text-gray-600">New</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-orange-600">$3,200</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Left Content - shows second on mobile, first on desktop */}
              <div className="order-1 lg:order-2 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
                <div className="inline-block mb-6">
                  <span className="bg-cyan-100 text-cyan-600 text-sm font-medium px-4 py-2 rounded-full">
                    For Creators
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12">
                  Monetize content with premium brands
                </h2>
                
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                      <Star className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Brand matching</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        Get matched with verified brands that value your content.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Simple processes</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                      Clear briefs, faster approvals, secure payments
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Growth and development</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                      Showcase your performance and grow your collaborations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 mx-4 mb-12 md:mx-8 lg:mx-16">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-pink-500 via-purple-500 to-purple-600 p-12 md:p-16 text-center shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to get started?
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Launch your first campaign or get to know Tblondi in two minutes — your shortcut to effortless influencer campaigns.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <Link href="/sign-up">
                  <Button 
                    size="lg"
                    className="relative bg-white hover:bg-gray-50 text-pink-600 font-semibold px-8 py-6 rounded-lg text-base shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  >
                  <span className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    Sign up for free
                  </span>
                </Button>
              </Link>
              
              <Link href="/v1/demo/creators">
                <Button 
                  size="lg"
                  variant="outline"
                  className="relative bg-transparent hover:bg-white/10 text-white border-2 border-white hover:border-white font-semibold px-8 py-6 rounded-lg text-base backdrop-blur-sm transition-all duration-300 transform hover:scale-105"
                >
                  <span className="flex items-center gap-2">
                    <Play className="h-5 w-5" />
                    View demo
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Custom animation styles */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        .animate-slide-in-left {
          animation: slide-in-left 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 1s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default Page;
